import { Database } from '@dolfin/business/db_types'
import { QueryKeys } from '@dolfin/hooks/queryKeys'
import useNotifications, {
  DateFilter,
  NotificationTypeFilter
} from '@dolfin/hooks/useNotifications'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Bell01, CheckCircle } from '@untitled-ui/icons-react'
import React, { useRef, useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { createPortal } from 'react-dom'
import { useQueryClient } from 'react-query'
import { useClickAway } from 'react-use'
import { zIndexes } from '../../constants/z-index'
import Dialog from '../../stories/Dialog'
import { IconWrapper } from '../../stories/IconWrapper'
import { Modal } from '../../stories/Modal'
import Select, { SelectItem } from '../../stories/Select'
import { Tab, TabList, Tabs } from '../../stories/Tabs'
import { getCurrentBreakpoint } from '../../stories/utils'

const Notifications = ({ isMobileUserView = false }) => {
  const [showNotifications, setShowNotifications] = useState(false)
  const currentBreakpoint = getCurrentBreakpoint()
  const { unreadNotifications } = useNotifications({})
  const notificationsRef = useRef<HTMLDivElement>(null)
  useClickAway(notificationsRef, () => setShowNotifications(false))

  return (
    <DialogTrigger>
      <div
        className={`relative cursor-pointer z-20 ${
          !isMobileUserView ? '-mr-2 ' : 'py-4 px-2 pr-4'
        }`}
        onClick={() => setShowNotifications(!showNotifications)}>
        <IconWrapper
          Icon={Bell01}
          height={18}
          color={isMobileUserView ? 'white' : undefined}
          className={'text-white md:text-brand-700'}
        />
        {unreadNotifications > 0 && (
          <p className='absolute -left-0.5 -top-1 text-[0.5rem] w-3 h-3 font-semibold rounded-full bg-red-500 text-center text-white align-middle'>
            {unreadNotifications}
          </p>
        )}
      </div>
      {currentBreakpoint === 'sm' &&
        createPortal(
          <Modal
            isOpen={showNotifications}
            onOpenChange={setShowNotifications}
            isDismissable={true}>
            <Dialog
              onCloseWithBtn={() => setShowNotifications(false)}
              onCloseIsXButton
              restrictWidth={'w-[90vw]'}>
              <NotificationContentBlock
                onClose={() => setShowNotifications(false)}
              />
            </Dialog>
          </Modal>,
          document.body
        )}
      {currentBreakpoint !== 'sm' &&
        showNotifications &&
        createPortal(
          <div
            ref={notificationsRef}
            className={`absolute top-6 right-6  ${zIndexes.MODALS}`}>
            <Dialog
              contentClassName={'p-0'}
              onCloseWithBtn={() => setShowNotifications(false)}
              restrictWidth={'w-[450px]'}
              onCloseIsXButton>
              <NotificationContentBlock
                onClose={() => setShowNotifications(false)}
              />
            </Dialog>
          </div>,
          document.body
        )}
    </DialogTrigger>
  )
}

interface NotificationContentBlockProps {
  entityId?: number
  profileId?: string
  entityType?: string
  type?: Database['public']['Enums']['notification_type']
  onClose?: () => void
  alwaysShowCommentBox?: boolean
  hideGoToButton?: boolean
}

export const NotificationContentBlock: React.FC<
  NotificationContentBlockProps
> = ({
  entityId,
  profileId,
  entityType,
  type,
  onClose,
  alwaysShowCommentBox,
  hideGoToButton
}) => {
  // Notification type options for the filter dropdown
  const notificationTypeOptions: {
    id: NotificationTypeFilter
    label: string
  }[] = [
    { id: 'all', label: t`All types` },
    { id: 'payout_flagged', label: t`Issues reported` },
    { id: 'payout_resolved', label: t`Issues resolved` },
    { id: 'payout_modified', label: t`Compensations modified` },
    { id: 'payout_paid', label: t`Compensations paid` },
    { id: 'payout_validated', label: t`Compensations validated` },
    { id: 'payout_adjustment_created', label: t`Adjustments created` },
    { id: 'statement_flagged', label: t`Statement issues` },
    { id: 'statement_resolved', label: t`Statement resolved` },
    { id: 'statement_approved', label: t`Statements approved` },
    { id: 'statement_paid', label: t`Statements paid` },
    { id: 'badge_won', label: t`Badges won` }
  ]

  const queryClient = useQueryClient()
  // When embedded in entity view (has entityId), default to 'all' so users can see all notifications
  // When in main inbox (no entityId), default to 'unread' for better UX
  const [dateFilter, setDateFilter] = useState<DateFilter>(
    entityId ? 'all' : 'unread'
  )
  const [typeFilter, setTypeFilter] = useState<NotificationTypeFilter>('all')

  const {
    notifications,
    groupedNotifications,
    writingComment,
    setWritingComment,
    NotificationItem,
    unreadNotifications,
    markAllAsRead,
    archiveNotification
  } = useNotifications({
    enabled: true,
    entity_id: entityId,
    entity_type: entityType,
    type: type,
    profile_id: profileId,
    dateFilter,
    typeFilter,
    // When embedded in entity view, include archived notifications so they remain visible
    includeArchived: !!entityId
  })

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header - only show when not embedded in entity view */}
      {!entityId && (
        <div className='sticky rounded-t-lg border-b z-20 top-0 py-3 bg-white'>
          {/* Title row with mark all as read */}
          <div className='flex flex-row w-full justify-between items-center mb-3 px-3'>
            <div className='flex flex-row items-center gap-2'>
              <span className='font-semibold text-base'>
                <Trans>Inbox</Trans>
              </span>
              {unreadNotifications !== undefined && unreadNotifications > 0 && (
                <span className='inline-flex items-center justify-center rounded-full text-white text-xs font-medium px-2 py-0.5 min-w-[20px] bg-red-500'>
                  {unreadNotifications}
                </span>
              )}
            </div>
            {unreadNotifications !== undefined && unreadNotifications > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className='text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-0.5 hover:underline'>
                <IconWrapper Icon={CheckCircle} height={16} />
                <Trans>Mark all read</Trans>
              </button>
            )}
          </div>

          {/* Filter tabs - full width */}
          <Tabs
            selectedKey={dateFilter}
            onSelectionChange={key => setDateFilter(key as DateFilter)}
            className='w-full'>
            <TabList className='w-full flex border-0 border-b border-neutral-200'>
              <Tab id='unread' className='flex-1 text-xs px-2 py-2 text-center'>
                <Trans>Unread</Trans>
              </Tab>
              <Tab id='7days' className='flex-1 text-xs px-2 py-2 text-center'>
                <Trans>7 days</Trans>
              </Tab>
              <Tab id='30days' className='flex-1 text-xs px-2 py-2 text-center'>
                <Trans>30 days</Trans>
              </Tab>
              <Tab id='all' className='flex-1 text-xs px-2 py-2 text-center'>
                <Trans>All</Trans>
              </Tab>
            </TabList>
          </Tabs>

          {/* Type filter dropdown */}
          <div className='mt-3 px-3'>
            <Select
              placeholder={t`Filter by type`}
              selectedKey={typeFilter}
              onSelectionChange={key =>
                setTypeFilter(key as NotificationTypeFilter)
              }
              items={notificationTypeOptions}
              className='w-full'>
              {item => (
                <SelectItem id={item.id} label={item.label}></SelectItem>
              )}
            </Select>
          </div>
        </div>
      )}

      {/* Notifications list with grouped sections */}
      <div
        className={`overflow-y-auto w-full flex-1 ${entityId ? '' : 'max-h-[80vh] md:max-h-[50vh]'}`}>
        {notifications?.length === 0 && (
          <div className='p-6 text-center'>
            <p className='text-neutral-500 text-sm'>
              {dateFilter === 'unread' ? (
                <Trans>
                  You&apos;re all caught up! No unread notifications.
                </Trans>
              ) : (
                <Trans>No notifications found for this period.</Trans>
              )}
            </p>
          </div>
        )}

        {/* Render grouped notifications */}
        {(() => {
          // When alwaysShowCommentBox is true, only the first notification in the list should show the reply box (single-issue view).
          const firstNotificationId =
            groupedNotifications?.[0]?.notifications?.[0]?.id ?? undefined
          return groupedNotifications?.map(group => (
            <div key={group.label}>
              {/* Group header - lower z-index than main header */}
              <div className='sticky top-0 z-10 bg-neutral-50 px-4 py-2 border-b border-neutral-100'>
                <span className='text-xs font-semibold text-neutral-600 uppercase tracking-wide'>
                  {group.label}
                </span>
              </div>
              {/* Group notifications */}
              {group.notifications.map(notification => (
                <NotificationItem
                  key={`notification_${notification.id}`}
                  notification={notification}
                  writingComment={
                    alwaysShowCommentBox
                      ? notification.id === firstNotificationId
                        ? firstNotificationId
                        : undefined
                      : writingComment
                  }
                  setWritingComment={setWritingComment}
                  reloadNotifications={async () => {
                    await queryClient.invalidateQueries(
                      QueryKeys.notifications.get
                    )
                  }}
                  onClose={onClose}
                  onArchive={
                    !alwaysShowCommentBox
                      ? () => archiveNotification(notification.id)
                      : undefined
                  }
                  hideGoToButton={hideGoToButton}
                />
              ))}
            </div>
          ))
        })()}
      </div>
    </div>
  )
}

export default Notifications
